import {AxiosError} from "axios";
import {useEffect, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';
import Modal from "react-bootstrap/Modal";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ApiConfig} from "../api/portal/ApiConfig";
import {AuthApi, PortalPasswordResetValidationResult} from "../api/portal/generated";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {XSRF} from "./Xsrf";

/*
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());

export const PortalPasswordReset = () =>
{
    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded reset validation code
     */
    const [resetCode, setResetCode] = useState<string>();
    /**
     * The result of the validation code
     */
    const [resetValidation, setResetValidation] = useState<PortalPasswordResetValidationResult>({email: ""});
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The password for the user
     */
    const [password, setPassword] = useState<string>();
    /**
     * The repeated password for the user
     */
    const [passwordRepeat, setPasswordRepeat] = useState<string>();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation('components');

    useEffect(() =>
    {
        const dParameter = searchParams.get("d");

        if (dParameter === null)
        {
            logger.warn("reset URL is missing the d parameter");

            setErrorMessage(t('portal-password-reset.reset-data-missing'));

            return;
        }

        authApi.authControllerValidatePortalPasswordReset(dParameter)
            .then(response =>
            {
                setResetCode(dParameter);

                const resetValidation = response.data;

                setResetValidation(resetValidation);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating reset link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
            })
    }, [searchParams, t]);

    const resetPassword = () =>
    {
        if (resetCode === undefined) {
            throw Error("resetCode is undefined");
        }

        if (password === undefined) {
            throw Error("password is undefined");
        }

        if (password !== passwordRepeat)
        {
            setErrorMessage(t('portal-password-reset.password-mismatch'));
            return;
        }

        // a XSRF cookie is required
        XSRF.generateCookie();

        authApi.authControllerResetPortalPassword(resetCode, password)
            .then(() => {
                    navigate("/");
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating reset link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    return (
        <Modal show={true} centered={true} animation={false}>
            <Modal.Header>
                <Modal.Title>{t('portal-password-reset.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                {/* show an alert if a validation error occurred */}
                <RenderAlert message={errorMessage}/>

                <FloatingLabel label={t('portal-password-reset.email-label')} className="text-secondary mb-3">
                    <Form.Control type="email" value={resetValidation.email} placeholder="Email"
                                  disabled={true}/>
                </FloatingLabel>

                <FloatingLabel label={t('portal-password-reset.password-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={password} placeholder="Password"
                                  onChange={e => setPassword(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t('portal-password-reset.password-repeat-label')} className="text-secondary mb-3">
                    <Form.Control type="password" value={passwordRepeat} placeholder="Password Repeat"
                                  onChange={e => setPasswordRepeat(e.target.value)}/>
                </FloatingLabel>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" disabled={StringUtils.isEmpty(resetValidation.email) || StringUtils.isEmpty(password)}
                        onClick={resetPassword}>
                    {t('portal-password-reset.set-password-button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
